import React, { ComponentProps, FC } from 'react';
import { BlockWithAlignableContents } from '@lexical/react/LexicalBlockWithAlignableContents';
import type {
    DOMConversionMap,
    DOMConversionOutput,
    DOMExportOutput,
    EditorConfig,
    LexicalNode,
    NodeKey,
    Spread,
} from 'lexical';
import { ElementFormatType, LexicalEditor } from 'lexical';
import {
    DecoratorBlockNode,
    SerializedDecoratorBlockNode,
} from '@lexical/react/LexicalDecoratorBlockNode';


export const InsertImageComponent: FC<
    { url: string; } &
    Omit<
        ComponentProps<typeof BlockWithAlignableContents>,
        'children'
    >
> = ({
    url,
    className,
    format,
    nodeKey
}) => {
    return (
        <BlockWithAlignableContents
            className={className}
            format={format}
            nodeKey={nodeKey}
        >
            <img
                src={url}
                className="insert-image"
                alt=""
                style={{width: "100%", maxWidth: "500px"}}
            />
        </BlockWithAlignableContents>
    );
}

export type SerializedInsertImageNode = Spread<
    {
        url: string;
        type: 'insert-image';
        version: 1;
    },
    SerializedDecoratorBlockNode
>;

export class InsertImageNode extends DecoratorBlockNode {
    __url: string;

    constructor(url: string, format?: ElementFormatType, key?: NodeKey) {
        super(format, key);
        this.__url = url;
    }

    static getType(): string {
        return 'insert-image'
    }

    static clone(node: InsertImageNode): InsertImageNode {
        return new InsertImageNode(node.__url, node.__format, node.__key);
    }

    static importJSON(serializedNode: SerializedInsertImageNode): InsertImageNode {
        const node = $createInsertImageNode(serializedNode.url);
        node.setFormat(serializedNode.format);
        return node;
    }

    exportJSON(): SerializedInsertImageNode {
        return {
            ...super.exportJSON(),
            type: 'insert-image',
            version: 1,
            url: this.__url
        }
    }

    // DOM→Nodeに変換
    static importDOM(): DOMConversionMap | null {
        return {
            span: (domNode: HTMLElement) => {
                if (!domNode.hasAttribute('data-insert-image')) return null;

                return {
                    conversion: (
                        domNode: HTMLElement,
                    ): DOMConversionOutput | null => {
                        const url = domNode.getAttribute('data-image-url');
                        if (!url) return null;

                        const node = $createInsertImageNode(url);
                        return { node };
                    },
                    priority: 1,
                };
            },
        };
    }

    // Node→DOMに変換（外部出力用）
    exportDOM(): DOMExportOutput {
        const element = document.createElement('div');
        element.setAttribute('data-insert-image', 'true');
        element.setAttribute('data-image-url', this.__url);
        return { element };
    }

    updateDOM(): false {
        return false
    }

    decorate(_editor: LexicalEditor, config: EditorConfig): JSX.Element {
        const embedBlockTheme = config.theme.embedBlock || {};
        const className = {
            base: embedBlockTheme.base || '',
            focus: embedBlockTheme.focus || ''
        };
        return (
            <InsertImageComponent
                className={className}
                format={this.__format}
                nodeKey={this.getKey()}
                url={this.__url}
            />
        )
    }
};

export const $createInsertImageNode = (url: string): InsertImageNode => {
    return new InsertImageNode(url);
}

export const $isInsertImageNode = (
    node: InsertImageNode | LexicalNode | null | undefined,
): node is InsertImageNode => {
    return node instanceof InsertImageNode;
}